import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Heart,
  Sparkles,
  ShieldCheck,
  Languages,
  Accessibility,
  Apple,
  ExternalLink,
  CheckCircle2,
  Users,
  Lock,
  MessageCircle,
  Globe2,
  ArrowRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_URL = "https://paypal.me/orgilla123";

const AMOUNT_OPTIONS = ["5", "10", "25", "50", "99"];

function classNames(...c) {
  return c.filter(Boolean).join(" ");
}

const FeaturePill = ({ icon: Icon, label, testId }) => (
  <div className="chip" data-testid={testId}>
    <Icon size={14} aria-hidden="true" />
    <span>{label}</span>
  </div>
);

const ProgressTracker = ({ stats, loading }) => {
  const total = stats?.total_raised ?? 0;
  const goal = stats?.goal ?? 99;
  const percent = Math.min(100, stats?.percent ?? 0);
  const donors = stats?.donor_count ?? 0;

  return (
    <div
      className="soft-card p-6 sm:p-8 w-full max-w-xl"
      data-testid="donation-progress-card"
      role="region"
      aria-label="Donation progress"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
          Live progress
        </div>
        <div className="chip">
          <Apple size={14} aria-hidden="true" />
          iOS unlock goal
        </div>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span
          className="font-heading text-4xl sm:text-5xl font-bold text-slate-900"
          data-testid="progress-amount-raised"
        >
          ${loading ? "—" : total.toFixed(0)}
        </span>
        <span className="text-slate-500 text-lg font-medium">
          raised of ${goal}
        </span>
      </div>
      <Progress
        value={percent}
        className="h-3 bg-slate-200"
        data-testid="progress-bar"
        aria-label={`${percent.toFixed(0)} percent of goal reached`}
      />
      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="text-slate-600 font-medium" data-testid="progress-percent">
          {percent.toFixed(0)}% complete
        </div>
        <div
          className="text-slate-600 font-medium flex items-center gap-1.5"
          data-testid="progress-donor-count"
        >
          <Users size={14} aria-hidden="true" />
          {donors} {donors === 1 ? "supporter" : "supporters"}
        </div>
      </div>
    </div>
  );
};

const SectionHeading = ({ eyebrow, title, description, testId }) => (
  <div className="max-w-3xl mb-12" data-testid={testId}>
    {eyebrow && (
      <div className="chip mb-4" data-testid={`${testId}-eyebrow`}>
        <Sparkles size={14} aria-hidden="true" />
        {eyebrow}
      </div>
    )}
    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
      {title}
    </h2>
    {description && (
      <p className="mt-5 text-lg text-slate-600 leading-relaxed">{description}</p>
    )}
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, bullets, accent, testId }) => (
  <Card className="soft-card border-0" data-testid={testId}>
    <CardContent className="p-8 sm:p-10">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
        style={{ background: accent }}
      >
        <Icon className="text-slate-900" size={26} aria-hidden="true" />
      </div>
      <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
        {title}
      </h3>
      <p className="text-slate-600 leading-relaxed mb-5">{description}</p>
      <ul className="space-y-2.5">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2.5 items-start text-slate-700">
            <CheckCircle2
              size={18}
              className="text-emerald-600 mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <span className="text-[15px]">{b}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

export default function LandingPage() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    amount: "5",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchStats = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/donations/stats`);
      setStats(res.data);
    } catch (e) {
      console.error("Failed to load stats", e);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/donations/stats`);
        if (!cancelled) setStats(res.data);
      } catch (e) {
        console.error("Failed to load stats", e);
      } finally {
        if (!cancelled) setLoadingStats(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToDonate = () => {
    const el = document.getElementById("donate");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) {
      e.email = "Please enter your email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Please enter a valid email address.";
    }
    const amt = parseFloat(form.amount);
    if (Number.isNaN(amt) || amt < 1) e.amount = "Please choose an amount of $1 or more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/donations`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        amount: parseFloat(form.amount),
      });
      setSubmitted(true);
      toast.success("Thank you! Your support is recorded.");
      // Open PayPal in a new tab so the user can complete payment
      window.open(PAYPAL_URL, "_blank", "noopener,noreferrer");
      // Refresh progress
      fetchStats();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", message: "", amount: "5" });
    setSubmitted(false);
    setErrors({});
  };

  const recentDonors = stats?.recent ?? [];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Nav */}
      <header className="w-full" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between">
          <a
            href="#top"
            className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900"
            data-testid="brand-link"
          >
            <span
              className="w-9 h-9 rounded-2xl flex items-center justify-center"
              style={{ background: "#d1fae5" }}
              aria-hidden="true"
            >
              <Accessibility className="text-emerald-700" size={18} />
            </span>
            <span>AccessTalk · DeepSense</span>
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <a href="#why" className="hover:text-slate-900 transition-colors" data-testid="nav-why">
              Why $99
            </a>
            <a
              href="#accesstalk"
              className="hover:text-slate-900 transition-colors"
              data-testid="nav-accesstalk"
            >
              AccessTalk
            </a>
            <a
              href="#deepsense"
              className="hover:text-slate-900 transition-colors"
              data-testid="nav-deepsense"
            >
              DeepSense
            </a>
            <a
              href="#accessibility-ai"
              className="hover:text-slate-900 transition-colors"
              data-testid="nav-accessibility-ai"
            >
              Accessibility AI
            </a>
          </nav>
          <Button
            onClick={scrollToDonate}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 h-auto font-semibold shadow-md shadow-emerald-600/20"
            data-testid="header-donate-button"
          >
            <Heart size={16} className="mr-1.5" aria-hidden="true" />
            Donate
          </Button>
        </div>
      </header>

      {/* HERO */}
      <section
        id="top"
        className="hero-backdrop relative overflow-hidden"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-10 pb-20 sm:pt-16 sm:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="chip mb-5" data-testid="hero-eyebrow">
                <Apple size={14} aria-hidden="true" />
                Remove the iOS $99 paywall
              </div>
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight"
                data-testid="hero-title"
              >
                Unlock Accessibility AI &amp; AccessTalk/DeepSense for iOS
              </h1>
              <p
                className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl"
                data-testid="hero-subtitle"
              >
                Help remove the Apple iOS paywall so Accessibility AI and
                AccessTalk/DeepSense can finally launch on iPhones — privacy-first,
                accessibility-first, and built for everyone.
              </p>

              <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-feature-pills">
                <FeaturePill icon={ShieldCheck} label="No data selling" testId="pill-no-data" />
                <FeaturePill icon={Languages} label="840+ languages" testId="pill-languages" />
                <FeaturePill icon={Accessibility} label="Accessibility-first" testId="pill-accessibility" />
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button
                  onClick={scrollToDonate}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 h-auto text-base font-semibold shadow-lg shadow-emerald-600/20"
                  data-testid="hero-donate-button"
                >
                  <Heart size={18} className="mr-2" aria-hidden="true" />
                  Donate &amp; help unlock iOS
                </Button>
                <a
                  href="#why"
                  className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 font-semibold px-2 py-2"
                  data-testid="hero-learn-more"
                >
                  Why $99 matters
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up fade-up-2">
              <ProgressTracker stats={stats} loading={loadingStats} />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section
        className="py-20 sm:py-24 bg-white"
        data-testid="intro-section"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="chip mb-4">
                <Sparkles size={14} aria-hidden="true" />
                The story
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                This isn’t a personal fundraiser.
              </h2>
            </div>
            <div className="md:col-span-7 space-y-5 text-lg text-slate-600 leading-relaxed">
              <p>
                I’m raising <span className="font-semibold text-slate-900">$99</span> to unlock the Apple Developer account so I can publish the iOS versions of{" "}
                <span className="font-semibold text-slate-900">Accessibility AI</span> and{" "}
                <span className="font-semibold text-slate-900">AccessTalk/DeepSense</span>.
              </p>
              <p>
                You’re not donating to me — you’re helping remove the Apple iOS paywall that blocks independent developers from releasing apps unless they pay a yearly fee.
              </p>
              <p>
                Once the $99 barrier is removed, everything goes live publicly on iOS, and updates keep rolling out on Google Play.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY $99 MATTERS */}
      <section
        id="why"
        className="py-20 sm:py-24"
        style={{ background: "linear-gradient(180deg, #f0fdf4 0%, #f8fafc 100%)" }}
        data-testid="why-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="Why $99 matters"
            title="One small fee. A massive accessibility unlock."
            description="iPhone users have been asking for a social platform and AI that doesn’t sell their data, doesn’t track them for ads, respects accessibility needs, and isn’t $16/month like Grok. The only thing standing in the way right now is Apple’s $99 developer fee."
            testId="why-heading"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Lock,
                title: "No data selling",
                body: "Your conversations, identity, and behavior are never collected for advertising.",
              },
              {
                icon: ShieldCheck,
                title: "No surveillance",
                body: "No engagement farming, no hostile algorithms, no creepy tracking.",
              },
              {
                icon: Accessibility,
                title: "Built for everyone",
                body: "Deaf, Blind, DeafBlind, ND, ESL, and mobility-impaired users — first, not last.",
              },
              {
                icon: Heart,
                title: "Fair pricing",
                body: "$2/month, not $16/month. Accessibility shouldn’t be a luxury subscription.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="soft-card p-6"
                data-testid={`why-card-${idx}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: "#ecfdf5" }}
                  aria-hidden="true"
                >
                  <item.icon className="text-emerald-700" size={20} />
                </div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg mb-1.5">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE CARDS — AccessTalk + DeepSense + Accessibility AI */}
      <section className="py-20 sm:py-28 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <SectionHeading
            eyebrow="What you’re unlocking"
            title="Three tools, one shared mission."
            description="A privacy-first social platform, the AI brain behind it, and a broader accessibility toolkit — all built so iPhone users can finally use them too."
            testId="products-heading"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div id="accesstalk">
              <FeatureCard
                icon={MessageCircle}
                accent="#d1fae5"
                title="AccessTalk"
                description="A privacy-first social platform — the alternative to Grok/X/Twitter that people have been asking for."
                bullets={[
                  "No selling or collecting personal data for ads",
                  "No surveillance-style tracking, no engagement farming",
                  "Community-first, accessibility-first design",
                  "Built for Deaf, DeafBlind, ND, ESL and multilingual users",
                  "$2/month subscription · $4 install on Google Play",
                ]}
                testId="card-accesstalk"
              />
            </div>

            <div id="deepsense">
              <FeatureCard
                icon={Sparkles}
                accent="#cffafe"
                title="DeepSense"
                description="The AI brain that powers AccessTalk — a multilingual, accessibility-focused alternative to Grok."
                bullets={[
                  "840+ languages, including Tok Pisin",
                  "ICT localization & culturally-aware translation",
                  "ND-friendly explanations, emotionally safe",
                  "Simplified or expanded responses on demand",
                  "Designed for privacy and user safety",
                ]}
                testId="card-deepsense"
              />
            </div>

            <div id="accessibility-ai">
              <FeatureCard
                icon={Accessibility}
                accent="#fef3c7"
                title="Accessibility AI"
                description="A separate, broader accessibility ecosystem with bots covering different categories of learning and understanding."
                bullets={[
                  "Already live on Google Play",
                  "For Deaf, HoH, Blind, ND, ESL, mobility-impaired users",
                  "Multiple bots across learning categories",
                  "Currently unavailable on iPhone — this changes that",
                  "Future-ready to connect with DeepSense",
                ]}
                testId="card-accessibility-ai"
              />
            </div>
          </div>

          <div
            className="mt-12 soft-card p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center gap-6"
            data-testid="ecosystem-callout"
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "#ecfdf5" }}
              aria-hidden="true"
            >
              <Globe2 className="text-emerald-700" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-slate-900 text-xl mb-1.5">
                One shared “brain” across platforms.
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Over time, <span className="font-semibold text-slate-900">DeepSense</span> will connect AccessTalk, Accessibility AI, and other tools — but right now, this fundraiser is specifically about getting Accessibility AI &amp; AccessTalk/DeepSense through the iOS gate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DONATE SECTION */}
      <section
        id="donate"
        className="py-20 sm:py-28"
        style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ecfdf5 100%)" }}
        data-testid="donate-section"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left: copy + progress + recent donors */}
            <div className="lg:col-span-5">
              <div className="chip mb-4" data-testid="donate-eyebrow">
                <Heart size={14} aria-hidden="true" />
                Help unlock iOS
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight"
                data-testid="donate-title"
              >
                Every dollar removes a barrier.
              </h2>
              <p
                className="mt-5 text-lg text-slate-600 leading-relaxed"
                data-testid="donate-description"
              >
                Drop your name, email, and (optional) message. We’ll record your pledge and open PayPal so you can complete the donation.
              </p>

              <div className="mt-8">
                <ProgressTracker stats={stats} loading={loadingStats} />
              </div>

              <div className="mt-8" data-testid="recent-donors">
                <div className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3">
                  Recent supporters
                </div>
                {recentDonors.length === 0 ? (
                  <p className="text-slate-500 text-[15px]" data-testid="recent-donors-empty">
                    Be the first to help remove the iOS paywall.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {recentDonors.map((d, i) => (
                      <li
                        key={i}
                        className="soft-card p-4 flex items-start gap-3"
                        data-testid={`recent-donor-${i}`}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-heading font-bold text-emerald-800"
                          style={{ background: "#d1fae5" }}
                          aria-hidden="true"
                        >
                          {d.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900">{d.name}</span>
                            <span className="text-emerald-700 font-bold text-sm">
                              ${Number(d.amount).toFixed(0)}
                            </span>
                          </div>
                          {d.message ? (
                            <p className="text-slate-600 text-[14px] mt-0.5 break-words">
                              “{d.message}”
                            </p>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Right: form / thank you */}
            <div className="lg:col-span-7">
              {!submitted ? (
                <form
                  onSubmit={handleSubmit}
                  className="soft-card p-8 sm:p-10"
                  data-testid="donation-form"
                  aria-label="Donation form"
                  noValidate
                >
                  <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 mb-2">
                    Your details
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Submitting opens PayPal in a new tab. The button below also lets you donate directly without filling the form.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="font-semibold text-slate-700 mb-2 block">
                        Name
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        className={classNames(
                          "h-12 rounded-2xl bg-slate-50 border-2 border-slate-200 px-4 text-base",
                          errors.name && "border-red-400"
                        )}
                        data-testid="form-input-name"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "name-error" : undefined}
                      />
                      {errors.name && (
                        <p
                          id="name-error"
                          className="text-sm text-red-600 mt-1.5"
                          data-testid="form-error-name"
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-semibold text-slate-700 mb-2 block">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@example.com"
                        className={classNames(
                          "h-12 rounded-2xl bg-slate-50 border-2 border-slate-200 px-4 text-base",
                          errors.email && "border-red-400"
                        )}
                        data-testid="form-input-email"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                      />
                      {errors.email && (
                        <p
                          id="email-error"
                          className="text-sm text-red-600 mt-1.5"
                          data-testid="form-error-email"
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-5">
                    <Label htmlFor="amount" className="font-semibold text-slate-700 mb-2 block">
                      Pledge amount (USD)
                    </Label>
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="amount-presets">
                      {AMOUNT_OPTIONS.map((amt) => {
                        const active = form.amount === amt;
                        return (
                          <button
                            type="button"
                            key={amt}
                            onClick={() => setForm({ ...form, amount: amt })}
                            className={classNames(
                              "px-4 py-2 rounded-full font-semibold text-sm border-2 transition-colors",
                              active
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"
                            )}
                            data-testid={`amount-preset-${amt}`}
                            aria-pressed={active}
                          >
                            ${amt}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      id="amount"
                      type="number"
                      min="1"
                      step="1"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className={classNames(
                        "h-12 rounded-2xl bg-slate-50 border-2 border-slate-200 px-4 text-base",
                        errors.amount && "border-red-400"
                      )}
                      data-testid="form-input-amount"
                      aria-invalid={!!errors.amount}
                      aria-describedby={errors.amount ? "amount-error" : undefined}
                    />
                    {errors.amount && (
                      <p
                        id="amount-error"
                        className="text-sm text-red-600 mt-1.5"
                        data-testid="form-error-amount"
                      >
                        {errors.amount}
                      </p>
                    )}
                  </div>

                  <div className="mt-5">
                    <Label htmlFor="message" className="font-semibold text-slate-700 mb-2 block">
                      Optional message
                    </Label>
                    <Textarea
                      id="message"
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Leave a note for the community (optional)"
                      className="rounded-2xl bg-slate-50 border-2 border-slate-200 p-4 text-base resize-none"
                      data-testid="form-input-message"
                    />
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 h-auto text-base font-semibold shadow-lg shadow-emerald-600/20 w-full sm:w-auto"
                      data-testid="form-submit-button"
                    >
                      {submitting ? (
                        "Recording your pledge…"
                      ) : (
                        <>
                          <Heart size={18} className="mr-2" aria-hidden="true" />
                          Submit &amp; donate via PayPal
                        </>
                      )}
                    </Button>
                    <a
                      href={PAYPAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full px-6 py-4 h-auto text-base font-semibold border-2 border-slate-200 text-slate-700 hover:border-emerald-400 hover:text-slate-900 transition-colors"
                      data-testid="form-paypal-direct-link"
                    >
                      Donate directly
                      <ExternalLink size={16} className="ml-2" aria-hidden="true" />
                    </a>
                  </div>

                  <p className="text-xs text-slate-500 mt-5">
                    Your email is used only for confirmation. We never sell or share it.
                  </p>
                </form>
              ) : (
                <div
                  className="soft-card p-8 sm:p-12 text-center"
                  data-testid="thank-you-card"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    style={{ background: "#d1fae5" }}
                    aria-hidden="true"
                  >
                    <CheckCircle2 className="text-emerald-700" size={32} />
                  </div>
                  <h3
                    className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
                    data-testid="thank-you-title"
                  >
                    Thank you!
                  </h3>
                  <p
                    className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto"
                    data-testid="thank-you-message"
                  >
                    Your donation has been received. Thank you for helping remove the iOS barrier and bringing a privacy-first, accessibility-focused social platform and AI alternative to more people.
                  </p>
                  <p className="text-sm text-slate-500 mt-4">
                    If PayPal didn’t open automatically, use the button below.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href={PAYPAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 h-auto text-base font-semibold shadow-lg shadow-emerald-600/20"
                      data-testid="thank-you-paypal-link"
                    >
                      Open PayPal
                      <ExternalLink size={16} className="ml-2" aria-hidden="true" />
                    </a>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="inline-flex items-center justify-center rounded-full px-6 py-4 h-auto text-base font-semibold border-2 border-slate-200 text-slate-700 hover:border-emerald-400 hover:text-slate-900 transition-colors"
                      data-testid="thank-you-reset-button"
                    >
                      Send another
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        className="py-12 bg-white border-t border-slate-100"
        data-testid="site-footer"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 font-heading font-bold text-slate-900">
              <span
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: "#d1fae5" }}
                aria-hidden="true"
              >
                <Accessibility className="text-emerald-700" size={16} />
              </span>
              AccessTalk · DeepSense · Accessibility AI
            </div>
            <p className="text-sm text-slate-500 mt-2 max-w-md">
              Privacy-first, accessibility-first. Built for the users mainstream tools ignore.
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <a
              href={PAYPAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-900 font-medium"
              data-testid="footer-paypal-link"
            >
              paypal.me/orgilla123
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
